/**
 * External IdP (Google / Apple) federation login.
 *
 * The SPA owns the login UI, so we can't deep-link Zitadel's hosted login
 * (that v1 UI can't read the login-v2 `V2_` auth requests our instance
 * mints — it 404s). Instead the button asks our backend to start a Zitadel
 * **idp_intent** and hand back the provider's authorization URL:
 *
 *   button click
 *     → POST /v1/platform/auth/idp/start { idpId, authRequestId, redirectBase }
 *     → { authUrl }                       (Google/Apple OAuth URL)
 *     → window.location = authUrl
 *     → user authenticates at the IdP
 *     → IdP → Zitadel /idps/callback → 302 to /auth/idp-callback?authRequest=&id=&token=
 *     → POST /v1/platform/auth/idp/finish → { callbackUrl }
 *     → window.location = callbackUrl → /auth/callback → PKCE → /app
 *
 * A provider's button only renders when its IdP id is configured at build
 * time (VITE_GOOGLE_IDP_ID / VITE_APPLE_IDP_ID) — those ids come from
 * registering the providers in Zitadel via scripts/register-zitadel-idps.sh
 * (see docs/runbooks/zitadel-idp-setup.md). No ids ⇒ no buttons, so we never
 * ship dead placeholders.
 */

import { api } from "@/lib/api-client";

const GOOGLE_IDP_ID = import.meta.env.VITE_GOOGLE_IDP_ID ?? "";
const APPLE_IDP_ID = import.meta.env.VITE_APPLE_IDP_ID ?? "";

export interface IdpProvider {
  id: string;
  label: string;
}

/** The providers that have a configured Zitadel IdP id, in display order. */
export function configuredIdps(): IdpProvider[] {
  const list: IdpProvider[] = [];
  if (GOOGLE_IDP_ID) list.push({ id: GOOGLE_IDP_ID, label: "Continue with Google" });
  if (APPLE_IDP_ID) list.push({ id: APPLE_IDP_ID, label: "Continue with Apple" });
  return list;
}

/**
 * Start an external-IdP login. Asks the backend to mint a Zitadel
 * idp_intent for the given provider + live AuthRequest, then navigates the
 * browser to the returned IdP authorization URL. Requires the live
 * AuthRequest id (the SPA receives it as `?authRequest=` on /login +
 * /signup in OIDC mode), so federation only works once Zitadel has minted
 * one. Throws on backend error so the caller can surface a toast.
 */
export async function startIdpLogin(
  idpId: string,
  authRequestId: string,
): Promise<void> {
  const resp = await api.post<{ authUrl: string }>(
    "/v1/platform/auth/idp/start",
    {
      idpId,
      authRequestId,
      // The backend builds the success/failure return URLs from this so
      // they land back on this SPA; it validates the value server-side.
      redirectBase: window.location.origin,
    },
  );
  window.location.assign(resp.authUrl);
}
